import { PropsWithChildren, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../features/auth/useAuth";
import { RoleLabel } from "../features/auth/types";
import { usePendingApprovalsCount } from "../features/approvals/hooks";
import "../App.css";

const Layout = ({ children }: PropsWithChildren) => {
  const { user, logout } = useAuth();
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    const closeIfDesktop = () => {
      if (typeof window !== "undefined" && window.innerWidth >= 769) {
        setNavOpen(false);
      }
    };
    window.addEventListener("resize", closeIfDesktop);
    return () => window.removeEventListener("resize", closeIfDesktop);
  }, []);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setNavOpen(false);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [navOpen]);

  const closeNav = () => setNavOpen(false);

  const showApprovals = user && (user.role === "superuser" || user.role === "retailer");
  const showLocationAdmins = user?.role === "superuser";
  const showMyUsers = user?.role === "retailer";
  const pendingCount = usePendingApprovalsCount().data ?? 0;
  const pendingBadge =
    showApprovals && pendingCount > 0 ? (
      <span
        className="app-header-badge-count"
        role="status"
        aria-label={`${pendingCount} pending request${pendingCount === 1 ? "" : "s"}`}
      >
        {pendingCount > 99 ? "99+" : pendingCount}
      </span>
    ) : null;

  return (
    <div className={`app-shell${navOpen && user ? " app-shell--nav-open" : ""}`}>
      <header className={`app-header${navOpen && user ? " app-header--nav-open" : ""}`}>
        <div className="app-header-brand">
          <span className="app-header-title">Provisioning Portal</span>
          {user ? (
            <span className="app-header-badge">Access level: {RoleLabel[user.role] ?? user.role}</span>
          ) : null}
        </div>

        {user ? (
          <>
            <button
              type="button"
              className="app-header-menu-toggle"
              aria-expanded={navOpen}
              aria-controls="app-header-mobile-nav"
              onClick={() => setNavOpen((o) => !o)}
            >
              <span className="visually-hidden">{navOpen ? "Close menu" : "Open menu"}</span>
              <span className="app-header-menu-icon" aria-hidden>
                <span />
                <span />
                <span />
              </span>
            </button>

            <div className="app-header-actions-desktop">
              <div className="app-header-user-pill">
                <div className="app-header-user-name">{user.fullName}</div>
                <div className="app-header-user-email">{user.email}</div>
              </div>
              {showApprovals ? (
                <Link to="/approvals" className="app-header-link-primary">
                  <span aria-hidden>✓</span>
                  Review requests
                  {pendingBadge}
                </Link>
              ) : null}
              {showLocationAdmins ? (
                <Link to="/location-admins" className="app-header-link-outline">
                  <span aria-hidden>⌂</span>
                  Location admins
                </Link>
              ) : null}
              {showLocationAdmins ? (
                <Link to="/activation" className="app-header-link-outline">
                  <span aria-hidden>◔</span>
                  Workvivo logins
                </Link>
              ) : null}
              {showMyUsers ? (
                <Link to="/location-users" className="app-header-link-outline">
                  <span aria-hidden>⌂</span>
                  My users
                </Link>
              ) : null}
              <Link to="/settings" className="app-header-link-outline">
                <span aria-hidden>⚙</span>
                Settings
              </Link>
              <button type="button" className="app-header-btn-outline" onClick={logout}>
                Log out
              </button>
            </div>

            <div id="app-header-mobile-nav" className="app-header-mobile-panel" role="navigation" aria-label="Account menu">
              <div className="app-header-user-pill">
                <div className="app-header-user-name">{user.fullName}</div>
                <div className="app-header-user-email">{user.email}</div>
              </div>
              <Link to="/" className="app-header-link-outline" onClick={closeNav}>
                <span aria-hidden style={{ fontSize: "1rem", lineHeight: 1 }}>
                  ⊞
                </span>
                Dashboard
              </Link>
              {showApprovals ? (
                <Link to="/approvals" className="app-header-link-primary" onClick={closeNav}>
                  <span aria-hidden>✓</span>
                  Review requests
                  {pendingBadge}
                </Link>
              ) : null}
              {showLocationAdmins ? (
                <Link to="/location-admins" className="app-header-link-outline" onClick={closeNav}>
                  <span aria-hidden>⌂</span>
                  Location admins
                </Link>
              ) : null}
              {showLocationAdmins ? (
                <Link to="/activation" className="app-header-link-outline" onClick={closeNav}>
                  <span aria-hidden>◔</span>
                  Workvivo logins
                </Link>
              ) : null}
              {showMyUsers ? (
                <Link to="/location-users" className="app-header-link-outline" onClick={closeNav}>
                  <span aria-hidden>⌂</span>
                  My users
                </Link>
              ) : null}
              <Link to="/settings" className="app-header-link-outline" onClick={closeNav}>
                <span aria-hidden>⚙</span>
                Settings
              </Link>
              <button
                type="button"
                className="app-header-btn-outline"
                onClick={() => {
                  closeNav();
                  logout();
                }}
              >
                Log out
              </button>
            </div>
          </>
        ) : null}
      </header>

      {navOpen && user ? (
        <div className="app-header-backdrop" aria-hidden onClick={closeNav} />
      ) : null}

      <main className="app-main">{children}</main>
    </div>
  );
};

export default Layout;
